import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { Col, Row, Form, Table, ButtonGroup, Button, Badge, InputGroup, Alert } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import Preloader from '../../components/Preloader/Preloader';
import { ProductContext } from '../../ProductProvider';
import { Helmet } from 'react-helmet-async';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useReactToPrint } from 'react-to-print';
import { coursesOffered } from '../../ProData';
import './issuereturn/IssueReturn.css';

const config = require('../../config.json');

export default function ReturnBooks() {
  const { validateLogin, loggedUser, generateDate, generateReturnDate, generateUUIDNo } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  const [bookResult, setBookResult] = useState([]);
  const [bookSelect, setBookSelect] = useState([]);
  const [searchBook, setSearchBook] = useState({ type: 'AccessionNo', value: '' });
  const [viewOption, setViewOption] = useState('All'); // 'All' | 'Today' | 'Over Due'
  const [loaded, setLoaded] = useState(true);

  // Sorting state - default: max fine on top, followed by max due date in desc order
  const [sortField, setSortField] = useState('fine');
  const [sortOrder, setSortOrder] = useState('desc');

  const componentRef = useRef(null);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    } else {
      handleBookFetch('NA', 'All');
    }
  }, [loggedUser]);

  // React to Print handler
  const reactPrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Circulation-Loans-Report-${new Date().toISOString().slice(0, 10)}`
  });

  // Fetch All Issued Books & Compute Overdue & Fine
  const handleBookFetch = async (value = 'NA', type = 'All') => {
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = 'VJCollege';

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/issue`, {
        params: {
          fetchKey: value,
          fetchOption: type,
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const rawData = Array.isArray(response.data) ? response.data : [];
      const todayDateStr = await generateDate();
      const todayDate = new Date(todayDateStr);

      const processed = rawData.map((item) => {
        const bookedOnDate = item.bookedOn ? new Date(item.bookedOn) : null;
        const bookedToDate = item.bookedTo ? new Date(item.bookedTo) : null;

        // Calculate total days since issued
        let totalDaysIssued = 0;
        if (bookedOnDate && !isNaN(bookedOnDate.getTime())) {
          const diffTime = todayDate - bookedOnDate;
          totalDaysIssued = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        }

        // Normalize primary fields
        const borrower = item.bookedfor || item.bookedFor || item.userName || item.applicantName || item.borrowerName || item.studentName || item.name || '';
        const userId = item.userID || item.userId || item.registrationNo || item.regNo || item.rollNo || item.memberId || '';
        const accessionNo = item.accessionNo || item.accNo || item.barcode || '';
        const title = item.bookTitle || item.title || '';
        const author = item.bookAuthor || item.author || '';

        // Determine loan period (in days) according to student category/program & book scope:
        // Reference Books:
        //   - Students: 1 Day Loan Period (Overdue fine starts after 1 day)
        //   - Staff / Faculty: 90 Days Loan Period
        // Circulation Books:
        //   - Staff / Faculty (VJS): 90 Days
        //   - DTH (Doctorate of Theology / Ph.D / Th.D): 60 Days
        //   - LTH (Licentiate in Sacred Theology / M.Th): 40 Days
        //   - BTH / OYT / RTD / General Students: 30 Days
        const reg = `${userId || ''} ${item.course || ''} ${item.department || ''}`.toUpperCase();
        const isStaff = reg.startsWith('VJS') || (item.userRole && item.userRole.toLowerCase().includes('staff'));
        const isReference = (item.bookScope || '').toLowerCase() === 'reference' || (item.scope || '').toLowerCase() === 'reference';

        let allowedLoanDays = 30; // default loan period

        if (isReference) {
          allowedLoanDays = isStaff ? 90 : 1;
        } else if (isStaff) {
          allowedLoanDays = 90; // Faculty / Staff - 90 Days
        } else if (reg.includes('DTH')) {
          allowedLoanDays = 60; // Doctorate of Theology (Ph.D / Th.D) - 60 Days
        } else if (reg.includes('LTH')) {
          allowedLoanDays = 40; // Licentiate in Sacred Theology (M.Th) - 40 Days
        } else if (reg.includes('BTH') || reg.includes('OYT') || reg.includes('RTD')) {
          allowedLoanDays = 30; // BTH, OYT, RTD - 30 Days
        } else {
          allowedLoanDays = 30; // Default
        }

        // Calculate overdue days based on the category loan period and bookedTo date
        let overdueDays = 0;
        if (bookedToDate && !isNaN(bookedToDate.getTime())) {
          const diffTo = todayDate - bookedToDate;
          if (diffTo > 0) {
            overdueDays = Math.max(0, Math.ceil(diffTo / (1000 * 60 * 60 * 24)));
          }
        } else if (totalDaysIssued > allowedLoanDays) {
          overdueDays = totalDaysIssued - allowedLoanDays;
        }

        // Fine is ₹1 per overdue day for students (Faculty is exempt or policy applies)
        const fine = (!isStaff && overdueDays > 0) ? overdueDays : (overdueDays > 0 ? overdueDays : 0);

        return {
          ...item,
          bookedfor: borrower,
          userID: userId,
          accessionNo: accessionNo,
          bookTitle: title,
          bookAuthor: author,
          bookScope: isReference ? 'Reference' : (item.bookScope || 'Circulation'),
          days: totalDaysIssued,
          allowedLoanDays: allowedLoanDays,
          overdueDays: overdueDays,
          fine: fine
        };
      });

      // Sort with max fine on top, followed by upcoming due date in order (soonest upcoming first)
      processed.sort((a, b) => {
        const fineDiff = (Number(b.fine) || 0) - (Number(a.fine) || 0);
        if (fineDiff !== 0) return fineDiff;

        const timeA = a.bookedTo ? new Date(a.bookedTo).getTime() : Infinity;
        const timeB = b.bookedTo ? new Date(b.bookedTo).getTime() : Infinity;
        return timeA - timeB;
      });

      setBookResult(processed);
      setBookSelect([]);
      setLoaded(true);
      enqueueSnackbar(`${processed.length} Active Loans Loaded`, { variant: 'success' });
    } catch (error) {
      console.error('Error fetching issued books:', error);
      enqueueSnackbar('Error loading circulation data: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  // KPIs
  const kpiStats = useMemo(() => {
    const totalActive = bookResult.length;
    const todayStr = new Date().toISOString().slice(0, 10);

    const dueToday = bookResult.filter((b) => {
      if (!b.bookedTo) return false;
      const bDate = new Date(b.bookedTo).toISOString().slice(0, 10);
      return bDate === todayStr;
    }).length;

    const overdueCount = bookResult.filter((b) => (b.fine || 0) > 0 || (b.overdueDays || 0) > 0).length;
    const totalFine = bookResult.reduce((sum, b) => sum + (b.fine || 0), 0);

    return { totalActive, dueToday, overdueCount, totalFine };
  }, [bookResult]);

  // Filtered and Sorted Data
  const displayedBooks = useMemo(() => {
    let result = [...bookResult];
    const todayStr = new Date().toISOString().slice(0, 10);

    // 1. Tab View Filter
    if (viewOption === 'Today') {
      result = result.filter((item) => {
        if (!item.bookedTo) return false;
        const bDate = new Date(item.bookedTo).toISOString().slice(0, 10);
        return bDate === todayStr;
      });
    } else if (viewOption === 'Over Due') {
      result = result.filter((item) => (item.fine || 0) > 0 || (item.overdueDays || 0) > 0);
    }

    // 2. Search Query Filter
    const query = (searchBook.value || '').trim().toLowerCase();
    if (query) {
      const topic = searchBook.type;
      result = result.filter((item) => {
        const borrowerName = (item.bookedfor || item.bookedFor || item.userName || item.applicantName || item.borrowerName || item.studentName || item.name || '').toLowerCase();
        const borrowerId = (item.userID || item.userId || item.registrationNo || item.regNo || item.rollNo || item.memberId || '').toLowerCase();
        const accessionNo = (item.accessionNo || item.accNo || item.barcode || '').toString().toLowerCase();
        const bookTitle = (item.bookTitle || item.title || '').toLowerCase();
        const bookAuthor = (item.bookAuthor || item.author || item.authors || '').toLowerCase();
        const issueId = (item.issueID || item.issueId || item.transactionID || item.id || '').toString().toLowerCase();
        const course = (item.course || item.department || item.dept || item.program || item.userID || '').toLowerCase();

        if (topic === 'AccessionNo') return accessionNo.includes(query);
        if (topic === 'BookTitle') return bookTitle.includes(query);
        if (topic === 'UserName' || topic === 'BorrowerName') return borrowerName.includes(query);
        if (topic === 'Author') return bookAuthor.includes(query);
        if (topic === 'UserID') return borrowerId.includes(query);
        if (topic === 'Course') return course.includes(query) || borrowerId.includes(query);
        if (topic === 'IssueID') return issueId.includes(query);

        // Fallback: search across all primary fields
        return (
          accessionNo.includes(query) ||
          bookTitle.includes(query) ||
          borrowerName.includes(query) ||
          borrowerId.includes(query) ||
          bookAuthor.includes(query)
        );
      });
    }

    // 3. Column Sorting
    result.sort((a, b) => {
      if (sortField === 'fine') {
        const fineDiff = (Number(b.fine) || 0) - (Number(a.fine) || 0);
        if (fineDiff !== 0) {
          return sortOrder === 'asc' ? -fineDiff : fineDiff;
        }
        // Secondary: upcoming due date in order (closest due date first)
        const timeA = a.bookedTo ? new Date(a.bookedTo).getTime() : Infinity;
        const timeB = b.bookedTo ? new Date(b.bookedTo).getTime() : Infinity;
        return timeA - timeB;
      }

      if (sortField === 'bookedTo' || sortField === 'bookedOn') {
        const timeA = a[sortField] ? new Date(a[sortField]).getTime() : 0;
        const timeB = b[sortField] ? new Date(b[sortField]).getTime() : 0;
        return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
      }

      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'accessionNo' || sortField === 'days' || sortField === 'overdueDays') {
        const numA = Number(valA) || 0;
        const numB = Number(valB) || 0;
        return sortOrder === 'asc' ? numA - numB : numB - numA;
      }

      const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      const cmp = naturalCollator.compare(String(valA), String(valB));
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [bookResult, viewOption, searchBook, sortField, sortOrder]);

  // Handle Sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1 small" style={{ opacity: 0.35 }}></i>;
    }
    return sortOrder === 'asc' ? (
      <i className="bi bi-arrow-up text-primary ms-1"></i>
    ) : (
      <i className="bi bi-arrow-down text-primary ms-1"></i>
    );
  };

  // Selection handlers
  const handleToggleSelectBook = (item) => {
    const isSelected = bookSelect.some((b) => b.accessionNo === item.accessionNo);
    if (isSelected) {
      setBookSelect((prev) => prev.filter((b) => b.accessionNo !== item.accessionNo));
    } else {
      const tbookingid = generateUUIDNo ? generateUUIDNo() : 'BK-' + Date.now();
      const staged = {
        ...item,
        newIssueID: 'BK-' + tbookingid
      };

      setBookSelect((prev) => {
        const next = [...prev, staged];
        if (next.length > 5) {
          enqueueSnackbar(`More than 5 books selected (${next.length} books). Please process 5 books at a time.`, { variant: 'warning' });
        }
        return next;
      });
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const stagedList = displayedBooks.map((item) => {
        const tbookingid = generateUUIDNo ? generateUUIDNo() : 'BK-' + Date.now();
        return {
          ...item,
          newIssueID: 'BK-' + tbookingid
        };
      });
      setBookSelect(stagedList);
      if (stagedList.length > 5) {
        enqueueSnackbar(`Selected ${stagedList.length} books. Please process 5 books at a time.`, { variant: 'warning' });
      }
    } else {
      setBookSelect([]);
    }
  };

  const removeDuplicates = (prop) => {
    const lookup = {};
    const result = [];
    for (const item of bookSelect) {
      const key = item[prop];
      if (!lookup[key]) {
        lookup[key] = true;
        result.push(item);
      }
    }
    return result;
  };

  // Process Return Action
  const handleBookReturns = async (event) => {
    if (event) event.preventDefault();

    if (!bookSelect || bookSelect.length === 0) {
      enqueueSnackbar('Please select at least one book to mark as Returned', { variant: 'info' });
      return;
    }

    if (bookSelect.length > 5) {
      enqueueSnackbar(`Please process 5 books at a time. You have selected ${bookSelect.length} books.`, { variant: 'warning' });
      return;
    }

    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const uniqueArray = removeDuplicates('userEmailID');
      const todayDate = await generateDate();

      const params = {
        clientID: loggedUser.website || 'VJCollege',
        bookSelect: bookSelect,
        bookStatus: 'Active',
        bookedTo: todayDate,
        updatedBy: loggedUser.nickname || loggedUser.userName || 'Librarian',
        updatedOn: todayDate,
        uniqueArray: uniqueArray
      };

      await axios.patch(`${config.api.invokeURL}/vjlibrary/returns`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const count = bookSelect.length;
      enqueueSnackbar(`Successfully processed return of ${count} book${count === 1 ? '' : 's'}!`, { variant: 'success' });
      setBookSelect([]);
      await handleBookFetch('NA', 'All');
    } catch (error) {
      console.error('Error returning books:', error);
      enqueueSnackbar('Error processing return: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  // Process Re-Issue / Renewal Action
  const handleBookReIssue = async (event) => {
    if (event) event.preventDefault();

    if (!bookSelect || bookSelect.length === 0) {
      enqueueSnackbar('Please select at least one book to Re-Issue / Renew', { variant: 'info' });
      return;
    }

    if (bookSelect.length > 5) {
      enqueueSnackbar(`Please process 5 books at a time. You have selected ${bookSelect.length} books.`, { variant: 'warning' });
      return;
    }

    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const uniqueArray = removeDuplicates('userEmailID');
      const todayDate = await generateDate();

      // Calculate return date per book based on allowed loan days (1 day for student reference books, 90 days for staff, 30/40/60 for others)
      const updatedBooks = await Promise.all(
        bookSelect.map(async (book) => {
          const days = book.allowedLoanDays || 30;
          const newDue = await generateReturnDate(days);
          return {
            ...book,
            bookedTo: newDue,
            newBookedTo: newDue
          };
        })
      );

      const returnDate = updatedBooks[0]?.newBookedTo || (await generateReturnDate(30));

      const params = {
        clientID: loggedUser.website || 'VJCollege',
        bookSelect: updatedBooks,
        bookStatus: 'Active',
        bookedTo: todayDate,
        updatedBy: loggedUser.nickname || loggedUser.userName || 'Librarian',
        updatedOn: todayDate,
        uniqueArray: uniqueArray,
        newBookedTo: returnDate
      };

      //console.log("Params:", params);

      await axios.post(`${config.api.invokeURL}/vjlibrary/reissue`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      // Notification
      axios.post(`${config.api.invokeURL}/vjlibrary/notification/returns`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      }).catch((e) => console.error('Notification error:', e));

      const count = bookSelect.length;
      enqueueSnackbar(`Successfully re-issued / renewed ${count} book${count === 1 ? '' : 's'} until ${returnDate}!`, { variant: 'success' });
      setBookSelect([]);
      await handleBookFetch('NA', 'All');
    } catch (error) {
      console.error('Error re-issuing books:', error);
      enqueueSnackbar('Error re-issuing books: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return date;
    }
  };

  const isAllSelected = displayedBooks.length > 0 && bookSelect.length === displayedBooks.length;

  return (
    <div className="container-fluid px-0">
      <Helmet>
        <title>Return & Re-Issue Books - Circulation Desk | Flynn - Meagher Library</title>
      </Helmet>

      {!loaded && <Preloader show={true} />}

      {/* KPI Dashboard Strip */}
      <div className="circulation-kpi-grid">
        <div className="circulation-kpi-card">
          <div className="circulation-kpi-icon bg-primary bg-opacity-10 text-primary">
            <i className="bi bi-journal-bookmark"></i>
          </div>
          <div>
            <div className="circulation-kpi-val">{kpiStats.totalActive}</div>
            <div className="circulation-kpi-lbl">Active Loans</div>
          </div>
        </div>

        <div className="circulation-kpi-card">
          <div className="circulation-kpi-icon bg-info bg-opacity-10 text-info">
            <i className="bi bi-calendar-event"></i>
          </div>
          <div>
            <div className="circulation-kpi-val">{kpiStats.dueToday}</div>
            <div className="circulation-kpi-lbl">Due Today</div>
          </div>
        </div>

        <div className="circulation-kpi-card">
          <div className="circulation-kpi-icon bg-danger bg-opacity-10 text-danger">
            <i className="bi bi-exclamation-triangle"></i>
          </div>
          <div>
            <div className="circulation-kpi-val text-danger">{kpiStats.overdueCount}</div>
            <div className="circulation-kpi-lbl">Overdue Books</div>
          </div>
        </div>

        <div className="circulation-kpi-card">
          <div className="circulation-kpi-icon bg-warning bg-opacity-10 text-warning">
            <i className="bi bi-currency-rupee"></i>
          </div>
          <div>
            <div className="circulation-kpi-val text-warning">₹{kpiStats.totalFine}</div>
            <div className="circulation-kpi-lbl">Total Fines</div>
          </div>
        </div>
      </div>

      {/* Main Filter & Action Card */}
      <div className="circulation-card">
        {/* Top Controls Header */}
        <div className="circulation-card-header flex-wrap gap-2">
          {/* Filter Pills */}
          <div className="d-flex align-items-center gap-2">
            <ButtonGroup size="sm">
              <Button
                variant={viewOption === 'All' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewOption('All')}
                className="fw-bold px-3"
              >
                All Loans ({bookResult.length})
              </Button>
              <Button
                variant={viewOption === 'Today' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewOption('Today')}
                className="fw-bold px-3"
              >
                Due Today ({kpiStats.dueToday})
              </Button>
              <Button
                variant={viewOption === 'Over Due' ? 'danger' : 'outline-secondary'}
                onClick={() => setViewOption('Over Due')}
                className="fw-bold px-3"
              >
                Overdue ({kpiStats.overdueCount})
              </Button>
            </ButtonGroup>
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={reactPrint}
              title="Print active loans list"
            >
              <i className="bi bi-printer me-1"></i> Print List
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleBookFetch('NA', 'All')}
              title="Refresh live loans"
            >
              <i className="bi bi-arrow-clockwise me-1"></i> Refresh
            </Button>
          </div>
        </div>

        <div className="circulation-card-body">
          {/* Search & Bulk Actions Bar */}
          <Row className="g-2 align-items-center mb-3">
            <Col lg={3} sm={5}>
              <Form.Select
                size="sm"
                className="circulation-input w-100"
                value={searchBook.type}
                onChange={(e) => setSearchBook({ ...searchBook, type: e.target.value, value: '' })}
              >
                <option value="AccessionNo">Accession # / Barcode</option>
                <option value="UserName">Borrower Name</option>
                <option value="UserID">Borrower ID</option>
                <option value="BookTitle">Book Title</option>
                <option value="Author">Book Author</option>
                <option value="Course">Program / Course</option>
                <option value="IssueID">Issue Transaction ID</option>
              </Form.Select>
            </Col>

            <Col lg={5} sm={7}>
              {searchBook.type === 'Course' ? (
                <Form.Select
                  size="sm"
                  className="circulation-input w-100"
                  value={searchBook.value}
                  onChange={(e) => setSearchBook({ ...searchBook, value: e.target.value })}
                >
                  <option value="">All Programs / Departments</option>
                  {coursesOffered.map((c) => (
                    <option key={c.courseCode} value={c.courseCode}>
                      {c.courseName} ({c.courseCode})
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    className="circulation-input border-start-0"
                    placeholder={`Filter by ${searchBook.type.replace(/([A-Z])/g, ' $1').trim()}...`}
                    value={searchBook.value}
                    onChange={(e) => setSearchBook({ ...searchBook, value: e.target.value })}
                  />
                  {searchBook.value && (
                    <Button variant="outline-secondary" onClick={() => setSearchBook({ ...searchBook, value: '' })}>
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </InputGroup>
              )}
            </Col>

            <Col lg={4} sm={12} className="d-flex justify-content-lg-end gap-2 mt-2 mt-lg-0">
              <Button
                variant={bookSelect.length > 5 ? 'outline-danger' : 'success'}
                size="sm"
                className="fw-bold px-3 d-inline-flex align-items-center gap-1"
                onClick={handleBookReturns}
                disabled={bookSelect.length === 0}
                title={bookSelect.length > 5 ? 'Batch limit: process maximum 5 books at a time' : 'Return selected books'}
              >
                <i className="bi bi-arrow-down-left-circle"></i>
                Return {bookSelect.length > 0 ? `(${bookSelect.length})` : ''}
              </Button>

              <Button
                variant={bookSelect.length > 5 ? 'outline-danger' : 'primary'}
                size="sm"
                className="fw-bold px-3 d-inline-flex align-items-center gap-1"
                onClick={handleBookReIssue}
                disabled={bookSelect.length === 0}
                title={bookSelect.length > 5 ? 'Batch limit: process maximum 5 books at a time' : 'Re-issue selected books'}
              >
                <i className="bi bi-arrow-repeat"></i>
                Re-Issue {bookSelect.length > 0 ? `(${bookSelect.length})` : ''}
              </Button>
            </Col>
          </Row>

          {/* Batch limit warning alert for librarian */}
          {bookSelect.length > 5 && (
            <Alert variant="warning" className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 px-3 mb-3 border-warning shadow-sm">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-triangle-fill text-warning fs-5"></i>
                <span className="text-dark">
                  <strong>Notice to Librarian:</strong> You have selected <strong>{bookSelect.length} books</strong>.
                  Please process a maximum of 5 books at a time.
                </span>
              </div>
              <Button
                variant="outline-dark"
                size="sm"
                className="py-1 px-2 fw-semibold"
                onClick={() => setBookSelect((prev) => prev.slice(0, 5))}
              >
                <i className="bi bi-check2-all me-1"></i>
                Select First 5 Only
              </Button>
            </Alert>
          )}

          {/* Loans Data Table */}
          <div ref={componentRef} className="circulation-table-card">
            <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
              <span className="fw-bold text-dark small">
                Showing <strong>{displayedBooks.length}</strong> active loan records
              </span>
              {bookSelect.length > 0 && (
                <Badge bg={bookSelect.length > 5 ? 'danger' : 'primary'} className="px-2 py-1">
                  {bookSelect.length} Book{bookSelect.length === 1 ? '' : 's'} Selected
                  {bookSelect.length > 5 && ' — Please process 5 books at a time'}
                </Badge>
              )}
            </div>

            {displayedBooks.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-check2-all text-success fs-1 d-block mb-2"></i>
                <h6>No Matching Loan Records Found</h6>
                <p className="small mb-0">All books are returned or no active loans match the filter.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover responsive className="circulation-table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }} className="text-center">
                        <Form.Check
                          inline
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          title="Select / Deselect all"
                        />
                      </th>
                      <th style={{ minWidth: '110px', cursor: 'pointer' }} onClick={() => handleSort('accessionNo')}>
                        Acc # {renderSortIcon('accessionNo')}
                      </th>
                      <th style={{ minWidth: '220px', cursor: 'pointer' }} onClick={() => handleSort('bookTitle')}>
                        Book Title {renderSortIcon('bookTitle')}
                      </th>
                      <th style={{ minWidth: '150px', cursor: 'pointer' }} onClick={() => handleSort('bookAuthor')}>
                        Author {renderSortIcon('bookAuthor')}
                      </th>
                      <th style={{ minWidth: '170px', cursor: 'pointer' }} onClick={() => handleSort('bookedfor')}>
                        Borrower & ID {renderSortIcon('bookedfor')}
                      </th>
                      <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleSort('bookedOn')}>
                        Issued {renderSortIcon('bookedOn')}
                      </th>
                      <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleSort('bookedTo')}>
                        Due Date {renderSortIcon('bookedTo')}
                      </th>
                      <th style={{ minWidth: '100px', cursor: 'pointer' }} className="text-center" onClick={() => handleSort('days')}>
                        Days Out {renderSortIcon('days')}
                      </th>
                      <th style={{ minWidth: '90px', cursor: 'pointer' }} className="text-center" onClick={() => handleSort('fine')}>
                        Fine {renderSortIcon('fine')}
                      </th>
                    </tr>
                  </thead>
                  <tbody key={`loans-tbody-${searchBook.type}-${searchBook.value || ''}-${viewOption}-${displayedBooks.length}`}>
                    {displayedBooks.map((item, idx) => {
                      const isSelected = bookSelect.some((b) => b.accessionNo === item.accessionNo);
                      const isOverdue = (item.fine || 0) > 0 || (item.overdueDays || 0) > 0;
                      const rowKey = `loan-${item.issueID || item.issueId || item.accessionNo || 'row'}-${item.userID || ''}-${idx}`;

                      return (
                        <tr
                          key={rowKey}
                          className={isOverdue ? 'row-overdue' : ''}
                          onClick={() => handleToggleSelectBook(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              inline
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelectBook(item)}
                            />
                          </td>
                          <td>
                            <span className="badge bg-light text-primary border font-monospace">
                              #{item.accessionNo}
                            </span>
                          </td>
                          <td>
                            <div className="fw-bold text-dark d-flex align-items-center gap-1 flex-wrap">
                              <span>{item.bookTitle}</span>
                              {((item.bookScope || '').toLowerCase() === 'reference' || (item.scope || '').toLowerCase() === 'reference') && (
                                <Badge bg="warning" text="dark" style={{ fontSize: '0.68rem' }}>
                                  Reference ({item.allowedLoanDays === 1 ? '1-Day Loan' : '90-Day Loan'})
                                </Badge>
                              )}
                            </div>
                            {item.bookClassificationNo && (
                              <div className="text-muted small font-monospace">
                                Call No: {item.bookClassificationNo}
                              </div>
                            )}
                          </td>
                          <td className="text-muted">{item.bookAuthor || 'Unknown'}</td>
                          <td>
                            <div className="fw-semibold text-dark">{item.bookedfor}</div>
                            <span className="badge bg-light text-muted border font-monospace">
                              {item.userID}
                            </span>
                          </td>
                          <td className="text-muted small">{formatDate(item.bookedOn)}</td>
                          <td>
                            <span className={`badge ${isOverdue ? 'bg-danger' : 'bg-info text-dark'}`}>
                              {formatDate(item.bookedTo)}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="small text-muted">{item.days} days</span>
                            {item.overdueDays > 0 && (
                              <div className="text-danger small fw-bold">
                                +{item.overdueDays}d overdue
                              </div>
                            )}
                          </td>
                          <td className="text-center">
                            {item.fine > 0 ? (
                              <span className="fine-badge">₹{item.fine}</span>
                            ) : (
                              <span className="fine-badge-none">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-light fw-bold">
                      <td colSpan={8} className="text-end text-dark">
                        Total Outstanding Fine for Displayed Loans:
                      </td>
                      <td className="text-center text-danger fs-6">
                        ₹{displayedBooks.reduce((sum, item) => sum + (item.fine || 0), 0)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
